import React, { useCallback, useState } from "react";
import TranslateIcon from "@mui/icons-material/Translate";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAdminReportsApi } from "../hooks/useAdminReportsApi";
import type { IncompleteTranslationRow } from "../shared/translationAdmin";
import EventsFormDialog from "./EventsFormDialog";

type AdminTranslationPanelProps = {
  /** When true, load list when panel mounts (accordion expanded). */
  active: boolean;
};

export default function AdminTranslationPanel({ active }: AdminTranslationPanelProps) {
  const { fetchIncompleteTranslations, bulkEnqueueTranslations } = useAdminReportsApi();
  const [rows, setRows] = useState<IncompleteTranslationRow[]>([]);
  const [totalIncomplete, setTotalIncomplete] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [excludeManufacturer, setExcludeManufacturer] = useState(true);
  const [dialogEventId, setDialogEventId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await fetchIncompleteTranslations({
        approved_only: approvedOnly,
        manufacturer_events: excludeManufacturer ? false : null,
        offset: 0,
        limit: 500,
      });
      if (!res) {
        setRows([]);
        setTotalIncomplete(0);
        return;
      }
      setRows(res.results ?? []);
      setTotalIncomplete(res.total_incomplete ?? 0);
      setSelected(new Set());
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      const hint =
        status === 403
          ? " Forbidden — assign the required translation API app role in Entra, then sign in again (see the info icon on the Translations accordion for role names)."
          : "";
      setError(`Failed to load incomplete translations.${hint}`);
      setRows([]);
      setTotalIncomplete(0);
    } finally {
      setLoading(false);
    }
  }, [fetchIncompleteTranslations, approvedOnly, excludeManufacturer]);

  React.useEffect(() => {
    if (active) {
      void load();
    }
  }, [active, load]);

  const allIds = rows.map((r) => r.global_event_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulkTranslate = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setError(null);
    setSuccess(null);
    setBulkBusy(true);
    try {
      const res = await bulkEnqueueTranslations(ids);
      if (!res) return;
      const nf = res.not_found_global_event_ids?.length
        ? ` Not found: ${res.not_found_global_event_ids.join(", ")}.`
        : "";
      setSuccess(
        `Queued translation for ${res.enqueued_count} event(s). Jobs run in the background.${nf}`
      );
      setSelected(new Set());
      await load();
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      const hint =
        status === 403
          ? " Permission denied — assign the required translation API app role in Entra, then sign out and in."
          : "";
      setError(`Bulk translate failed.${hint}`);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Box sx={{ px: 1.5, py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        <TranslateIcon color="primary" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Events missing at least one of <strong>ger / eng / ita / cze</strong>. Select rows and queue
          Azure translation (same as per-event &quot;translate now&quot;).
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={approvedOnly}
              onChange={(_, v) => setApprovedOnly(v)}
            />
          }
          label="Approved events only"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={excludeManufacturer}
              onChange={(_, v) => setExcludeManufacturer(v)}
            />
          }
          label="Exclude manufacturer events"
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          Refresh list
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={selected.size === 0 || bulkBusy}
          onClick={() => void runBulkTranslate()}
          startIcon={bulkBusy ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Translate selected ({selected.size})
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Total incomplete (all pages): <strong>{totalIncomplete}</strong>
        {rows.length < totalIncomplete ? " — showing first page only (up to 500)." : ""}
      </Typography>

      {loading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && !allSelected}
                  checked={allSelected}
                  onChange={toggleAll}
                  inputProps={{ "aria-label": "select all" }}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Event Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Missing</TableCell>
              <TableCell>Present</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.global_event_id} hover selected={selected.has(r.global_event_id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(r.global_event_id)}
                    onChange={() => toggleOne(r.global_event_id)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                    onClick={() => setDialogEventId(r.global_event_id)}
                  >
                    {r.global_event_id}
                  </Button>
                </TableCell>
                <TableCell>{r.event_code}</TableCell>
                <TableCell>{r.event_type}</TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {r.languages_missing.map((c) => (
                      <Chip key={c} size="small" color="warning" label={c} variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {r.languages_present.map((c) => (
                      <Chip key={c} size="small" label={c} variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!loading && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No events with incomplete language coverage (for current filters).
        </Typography>
      )}

      <EventsFormDialog
        open={dialogEventId != null}
        eventId={dialogEventId}
        createNewEvent={false}
        onClose={() => setDialogEventId(null)}
        onNavigateToEvent={(id) => setDialogEventId(id)}
      />
    </Box>
  );
}
